import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'

import { cn, getDocumentTypeColor, getDocumentTypeText } from '@/lib/utils'
import type { DocumentData, DocumentTypeTag, JobStatus } from '@/data/entities'

const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-full border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden',
  {
    variants: {
      variant: {
        default:
          'border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary-hovered',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary-hovered',
        secondaryGreen:
          'border-transparent bg-secondary-green text-secondary-green-foreground [a&]:hover:bg-secondary-green-hovered',
        accentNavy:
          'border-transparent bg-accent-navy text-accent-navy-foreground [a&]:hover:bg-accent-navy-hovered',
        destructive:
          'border-transparent bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        outline:
          'text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: React.ComponentProps<'span'> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'span'

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  )
}

function DocumentTypeBadge({
  type,
  className,
}: {
  type: DocumentTypeTag
  className?: string
}) {
  return (
    <Badge className={cn(getDocumentTypeColor(type), className)}>
      {getDocumentTypeText(type)}
    </Badge>
  )
}

type DocumentStatusBadgeProps = {
  status: DocumentData['status']
}

function DocumentStatusBadge({ status }: DocumentStatusBadgeProps) {
  switch (status) {
    case 'PENDING':
      return (
        <Badge variant="outline" className="gap-1 opacity-80">
          <Loader2 className="h-3 w-3 animate-spin" />
          Pending
        </Badge>
      )
    case 'PROCESSING':
      return (
        <Badge variant="outline" className="gap-1">
          <Loader2 className="h-3 w-3 animate-spin" />
          Processing
        </Badge>
      )
    case 'ERROR':
      return (
        <Badge
          variant="outline"
          className="text-destructive border-destructive"
        >
          Error
        </Badge>
      )
    case 'COMPLETED':
      return <Badge variant="outline">Completed</Badge>
    default:
      return <Badge variant="outline">{status}</Badge>
  }
}

type JobStatusBadgeProps = {
  status: JobStatus | undefined
  className?: string
}

/**
 * Maps the worker-pool canonical `JobStatus` to a coloured Badge. Used
 * by table columns and the per-document Jobs detail panel.
 *
 * Kept as a pure React component for use inline in tables; the
 * `DocumentJobsPanel` Rust port has its own mirrored variant in
 * `wasm/src/components/ui/badge.rs`.
 */
function JobStatusBadge({ status, className }: JobStatusBadgeProps) {
  if (!status) {
    return (
      <Badge variant="outline" className={cn('opacity-60', className)}>
        —
      </Badge>
    )
  }
  switch (status) {
    case 'PENDING':
      return (
        <Badge variant="outline" className={cn('gap-1 opacity-80', className)}>
          <Loader2 className="h-3 w-3 animate-spin" />
          Pending
        </Badge>
      )
    case 'RUNNING':
      return (
        <Badge variant="outline" className={cn('gap-1', className)}>
          <Loader2 className="h-3 w-3 animate-spin" />
          Running
        </Badge>
      )
    case 'DONE':
      return (
        <Badge
          variant="outline"
          className={cn('border-success text-success', className)}
        >
          Done
        </Badge>
      )
    case 'FAILED':
      return (
        <Badge
          variant="outline"
          className={cn('text-destructive border-destructive', className)}
        >
          Failed
        </Badge>
      )
    case 'BLOCKED':
      return (
        <Badge
          variant="outline"
          className={cn('border-amber-500 text-amber-600', className)}
        >
          Blocked
        </Badge>
      )
    case 'PARTIAL':
      return (
        <Badge
          variant="outline"
          className={cn('border-amber-500 text-amber-600', className)}
        >
          Partial
        </Badge>
      )
    case 'NOT_APPLICABLE':
      return (
        <Badge variant="outline" className={cn('opacity-60', className)}>
          N/A
        </Badge>
      )
    default:
      return (
        <Badge variant="outline" className={className}>
          {status}
        </Badge>
      )
  }
}

export {
  Badge,
  DocumentTypeBadge,
  DocumentStatusBadge,
  JobStatusBadge,
  badgeVariants,
}
